import { useState } from 'react'

import { Alert } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faSpinner, faUserCircle } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'

import '../../assets/profile-selection.scss'

/**
 * Step 2 of the multi-profile login: the account is authenticated but holds several profiles, and
 * the user picks the one their token gets bound to. Used by the local login (LoginView) as well as
 * by the SSO login of external accounts (AuthCallback).
 */
const ProfileSelection = ({ profiles, loading, onSelect, onBack }) => {
  const t = useSelector(selectTranslate)

  const [selectedId, setSelectedId] = useState(null)

  const getRolesLabel = profile => (Array.isArray(profile.roles) ? profile.roles : [])
    .filter(r => r !== 'ROLE_USER')
    .map(r => t(r))
    .join(', ')

  const getOrganizationsLabel = profile => [
    profile.school ? profile.school.name : null,
    ...(Array.isArray(profile.institutions) ? profile.institutions.map(i => i.name) : [])
  ].filter(o => o).join(', ')

  const handleSelect = profile => {
    if (loading || typeof onSelect !== 'function') { return }

    setSelectedId(profile.id)
    onSelect(profile.id)
  }

  const renderBack = () => typeof onBack === 'function' && (
    <div className='profile-selection__back'>
      <a href='#/' className='useful-link' onClick={e => { e.preventDefault(); onBack() }}>
        {t('profile_selection.back')}
      </a>
    </div>
  )

  // Valid credentials but no active profile left on the account (all deleted): the account is
  // unusable as is, the user has to contact their organization.
  if (!Array.isArray(profiles) || profiles.length === 0) {
    return (
      <div className='profile-selection'>
        <Alert
          type='warning'
          showIcon
          message={t('profile_selection.no_active_profile.title')}
          description={t('profile_selection.no_active_profile.description')}
        />
        {renderBack()}
      </div>
    )
  }

  return (
    <div className='profile-selection'>
      <div className='profile-selection__header'>
        <div className='profile-selection__title'>{t('profile_selection.title')}</div>
        <div className='profile-selection__subtitle'>{t('profile_selection.subtitle')}</div>
      </div>

      <div className='profile-selection__list'>
        {profiles.map(profile => {
          const isActive = selectedId === profile.id
          const organizations = getOrganizationsLabel(profile)

          return (
            <div
              key={profile.id}
              className={`profile-card${isActive ? ' is-active' : ''}${loading && !isActive ? ' is-dimmed' : ''}`}
              role='button'
              tabIndex={0}
              onClick={() => handleSelect(profile)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleSelect(profile)
                }
              }}
            >
              <div className='profile-card__avatar'>
                <FontAwesomeIcon icon={faUserCircle} />
              </div>
              <div className='profile-card__body'>
                <div className='profile-card__role'>{getRolesLabel(profile) || profile.email}</div>
                {organizations && <div className='profile-card__org'>{organizations}</div>}
              </div>
              <FontAwesomeIcon
                className='profile-card__chevron'
                icon={loading && isActive ? faSpinner : faChevronRight}
                spin={loading && isActive}
              />
            </div>
          )
        })}
      </div>

      {renderBack()}
    </div>
  )
}

export default ProfileSelection
